/**
 * Versión del layout del portal.
 *
 * - 1: layout de master (menú horizontal, marca Beliv, colores de master).
 * - 2: layout nuevo (home-layout, marca B.R.A.I.N., colores de BRAIN).
 *
 * La versión por defecto la elige la configuración de build: se construye la
 * 2 y con la configuración "layout-v1" la 1 (`ng build -c production,layout-v1`,
 * ver angular.json), que cambia el index (`src/index.html` →
 * `src/index.v1.html`) y lo deja en `<html data-layout-version="…">`.
 *
 * Encima de eso, el switch del header (solo administradores) guarda la otra
 * versión en localStorage y recarga. Para que eso funcione sin otro build, el
 * build trae las dos hojas globales (`styles.v1.scss` / `styles.v2.scss`, sin
 * inyectar) y `loadLayoutStyles()` carga la que corresponde antes de arrancar.
 *
 * Vive en su propio archivo porque la leen `app-config.ts` (para armar la
 * config por defecto) y `main.ts` (para cargar los estilos e invalidar la
 * config guardada en localStorage al cambiar de versión).
 */
export type LayoutVersion = 1 | 2;

/** Clave de localStorage con la versión elegida desde el switch del header */
const LAYOUT_VERSION_KEY = 'layoutVersion';

/** La versión con la que se construyó el index */
export const BUILD_LAYOUT_VERSION: LayoutVersion =
  document.documentElement.getAttribute('data-layout-version') === '1' ? 1 : 2;

/** Lo elegido en el switch, si hay algo válido guardado */
function readStoredLayoutVersion(): LayoutVersion | null {
  try {
    const stored = localStorage.getItem(LAYOUT_VERSION_KEY);
    return stored === '1' ? 1 : stored === '2' ? 2 : null;
  } catch {
    return null;
  }
}

export const LAYOUT_VERSION: LayoutVersion = readStoredLayoutVersion() ?? BUILD_LAYOUT_VERSION;

// Lo que lea el atributo (estilos, otros scripts) ve la versión efectiva
document.documentElement.setAttribute('data-layout-version', String(LAYOUT_VERSION));

// Clave que se guarda en localStorage; si cambia, se descarta la config vieja
export const LAYOUT_CONFIG_VERSION = `home-layout-v${LAYOUT_VERSION}`;

/**
 * Guarda la versión elegida en el switch. Hay que recargar para aplicarla:
 * la config, los estilos y el layout se arman al arrancar.
 * @param version Versión a usar desde la próxima carga
 */
export function setLayoutVersion(version: LayoutVersion): void {
  try {
    if (version === BUILD_LAYOUT_VERSION) {
      localStorage.removeItem(LAYOUT_VERSION_KEY);
    } else {
      localStorage.setItem(LAYOUT_VERSION_KEY, String(version));
    }
  } catch {
    // Sin localStorage el switch no puede recordar nada: queda la del build
  }
}

/**
 * Carga la hoja global de la versión efectiva (`layout-v1.css` /
 * `layout-v2.css`) y cambia el ícono de la pestaña.
 *
 * La hoja se inserta antes de `styles.css` para respetar el orden de
 * angular.json: los CSS de las librerías van después y pueden pisarla. Se
 * resuelve cuando la hoja terminó de cargar, así la app no arranca sin
 * estilos; si falla, arranca igual para no dejar la pantalla en blanco.
 */
export function loadLayoutStyles(): Promise<void> {
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) {
    icon.href = LAYOUT_VERSION === 1 ? 'favicon.ico' : 'assets/images/logo/brain_logo.png';
  }

  return new Promise(resolve => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    // Las hojas sin inyectar salen sin hash: el del bundle principal evita
    // que el navegador sirva la de un deploy anterior
    link.href = `layout-v${LAYOUT_VERSION}.css${cacheBuster()}`;
    link.onload = () => resolve();
    link.onerror = () => {
      console.error(`No se pudo cargar la hoja del layout ${LAYOUT_VERSION}`);
      resolve();
    };

    const vendorStyles = document.querySelector('link[rel="stylesheet"][href^="styles"]');
    document.head.insertBefore(link, vendorStyles);
  });
}

/** `?v=<hash>` con el hash del bundle principal, o nada si no lo tiene (dev) */
function cacheBuster(): string {
  const main = document.querySelector<HTMLScriptElement>('script[src^="main"]');
  const hash = main?.getAttribute('src')?.match(/^main\.([^.]+)\.js/)?.[1];
  return hash ? `?v=${hash}` : '';
}
